// Facebook Pixel integration — Tier 2 analytics (requires explicit consent).
// Pixel ID is read from VITE_FB_PIXEL_ID. If unset, all calls are no-ops.
// Complies with Senegal Loi n°2008-12: only initialised after analytics consent.

import { isConsentGiven } from "@/lib/consent-manager";

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: (...args: unknown[]) => void;
  }
}

const PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID as string | undefined;

let initialised = false;

// ─── Script loading ──────────────────────────────────────────────────

function loadFbqScript(): void {
  if (document.getElementById("fb-pixel-script")) return;

  // Standard Facebook Pixel base code (minified inline version)
  // Source: https://developers.facebook.com/docs/facebook-pixel/implementation
  const n = (window.fbq = function (...args: unknown[]) {
    if (n.callMethod) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (n as any).callMethod(...args);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (n as any).queue.push(args);
    }
  }) as unknown as typeof window.fbq & {
    callMethod?: (...args: unknown[]) => void;
    push?: (...args: unknown[]) => void;
    loaded?: boolean;
    version?: string;
    queue?: unknown[];
  };

  if (!window._fbq) window._fbq = window.fbq;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];

  const script = document.createElement("script");
  script.id = "fb-pixel-script";
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";

  const firstScript = document.getElementsByTagName("script")[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);

  // noscript fallback image tag
  if (!document.getElementById("fb-pixel-noscript")) {
    const noscript = document.createElement("noscript");
    noscript.id = "fb-pixel-noscript";
    const img = document.createElement("img");
    img.height = 1;
    img.width = 1;
    img.style.display = "none";
    img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.insertBefore(noscript, document.body.firstChild);
  }
}

// ─── Public API ──────────────────────────────────────────────────────

/**
 * Initialises Facebook Pixel. Safe to call multiple times — subsequent
 * calls are no-ops once the pixel is already running.
 *
 * Gated on:
 *  - VITE_FB_PIXEL_ID env var being set
 *  - User having granted "analytics" consent (Loi 2008-12)
 */
export function initFacebookPixel(): void {
  if (!PIXEL_ID) {
    if (import.meta.env.DEV) {
      console.warn("[FB Pixel] VITE_FB_PIXEL_ID not set — pixel disabled");
    }
    return;
  }

  if (!isConsentGiven("analytics")) return;
  if (initialised) return;

  loadFbqScript();
  window.fbq("init", PIXEL_ID);
  window.fbq("track", "PageView");
  initialised = true;
}

/** Tears down the pixel when consent is revoked. */
export function shutdownFacebookPixel(): void {
  initialised = false;
  // fbq itself cannot be "unloaded" once the script is in the DOM,
  // but we stop sending further events by resetting the initialised flag.
}

/** Returns true only when the pixel has been initialised and is ready. */
function isReady(): boolean {
  return initialised && typeof window.fbq === "function";
}

// ─── Standard e-commerce event helpers ──────────────────────────────

/** Track a page view. Call on every route change. */
export function fbTrackPageView(): void {
  if (!isReady()) return;
  window.fbq("track", "PageView");
}

/**
 * Track a product detail page view.
 * @param contentId   SKU or product ID
 * @param contentName Human-readable product name
 * @param valueFcfa   Product price in FCFA
 */
export function fbTrackViewContent(
  contentId: string,
  contentName: string,
  valueFcfa: number
): void {
  if (!isReady()) return;
  window.fbq("track", "ViewContent", {
    content_ids: [contentId],
    content_name: contentName,
    content_type: "product",
    currency: "XOF",
    value: valueFcfa,
  });
}

/**
 * Track an add-to-cart action.
 * @param contentId  SKU or product ID
 * @param valueFcfa  Line total in FCFA (price × quantity)
 * @param quantity   Number of units added
 */
export function fbTrackAddToCart(
  contentId: string,
  valueFcfa: number,
  quantity: number
): void {
  if (!isReady()) return;
  window.fbq("track", "AddToCart", {
    content_ids: [contentId],
    content_type: "product",
    currency: "XOF",
    value: valueFcfa,
    num_items: quantity,
  });
}

/**
 * Track the start of checkout.
 * @param cartTotalFcfa  Total cart value in FCFA
 * @param numItems       Total number of items
 */
export function fbTrackInitiateCheckout(
  cartTotalFcfa: number,
  numItems: number
): void {
  if (!isReady()) return;
  window.fbq("track", "InitiateCheckout", {
    currency: "XOF",
    value: cartTotalFcfa,
    num_items: numItems,
  });
}

/**
 * Track a completed purchase.
 * @param orderRef       Unique order reference
 * @param totalFcfa      Order total in FCFA
 * @param contentIds     Array of SKU/product IDs purchased
 */
export function fbTrackPurchase(
  orderRef: string,
  totalFcfa: number,
  contentIds: string[]
): void {
  if (!isReady()) return;
  window.fbq("track", "Purchase", {
    content_ids: contentIds,
    content_type: "product",
    currency: "XOF",
    value: totalFcfa,
    order_id: orderRef,
  });
}

// ─── Analytics provider bridge ───────────────────────────────────────

/**
 * Returns an AnalyticsProvider-compatible function that maps generic
 * trackEvent() calls to Facebook Pixel standard events.
 *
 * Register via: registerAnalyticsProvider(createFacebookPixelProvider())
 * in main.tsx after consent is confirmed.
 *
 * Event name mapping:
 *  product_viewed       → ViewContent
 *  added_to_cart        → AddToCart
 *  checkout_started     → InitiateCheckout
 *  order_completed      → Purchase
 *  (all others)         → ignored (no noise in Pixel)
 */
export function createFacebookPixelProvider(): (
  name: string,
  properties?: Record<string, unknown>
) => void {
  return (name: string, properties?: Record<string, unknown>) => {
    if (!isReady()) return;

    switch (name) {
      case "product_viewed": {
        const id = String(properties?.product_id ?? "");
        const label = String(properties?.content_name ?? properties?.product_id ?? "");
        const price = Number(properties?.price_fcfa ?? 0);
        fbTrackViewContent(id, label, price);
        break;
      }
      case "added_to_cart": {
        const id = String(properties?.product_id ?? "");
        const qty = Number(properties?.quantity ?? 1);
        const price = Number(properties?.price_fcfa ?? 0);
        fbTrackAddToCart(id, price * qty, qty);
        break;
      }
      case "checkout_started": {
        const total = Number(properties?.cart_total_fcfa ?? 0);
        const items = Number(properties?.item_count ?? 0);
        fbTrackInitiateCheckout(total, items);
        break;
      }
      case "order_completed": {
        const ref = String(properties?.order_ref ?? "");
        const total = Number(properties?.total_fcfa ?? 0);
        const ids = Array.isArray(properties?.content_ids)
          ? (properties.content_ids as string[])
          : [];
        fbTrackPurchase(ref, total, ids);
        break;
      }
      default:
        // Deliberately ignored — only standard e-commerce events are mapped
        break;
    }
  };
}
